import { useState } from 'react';
import { create } from '@bufbuild/protobuf';
import { App, Button, Form, Input, Modal } from 'antd';
import { Zap } from 'lucide-react';
import MaasModelSelect from '@/components/MaasModelSelect';
import { useProbeMaasModels } from '../hooks/useMaasModels';
import { useCreateMaasCredential } from '../hooks/useMaasCredentials';
import { maasApi } from '@/api/maas.api';
import { CreateMaasCredentialRequestSchema } from '@/proto/agentplatform/httpv2/maas_pb';
import { type MaasModel } from '@/proto/agentplatform/v2/maas_pb';
import { ApiError } from '@/api/errors';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DEFAULT_BASE_URL = 'https://cloud.infini-ai.com/maas/v1';

interface FormValues {
  name: string;
  apiKey: string;
  baseUrl: string;
  defaultModel: string;
}

export default function CreateCredentialDialog({ open, onClose, onSuccess }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [models, setModels] = useState<MaasModel[]>([]);

  const probe = useProbeMaasModels();
  const createMut = useCreateMaasCredential();

  // 名称唯一性预检（全局）。校验接口异常不阻塞创建（create 路径仍会强制校验）。
  const validateName = async (_: unknown, value: string) => {
    if (!value) return;
    let available: boolean;
    try {
      available = await maasApi.checkName(value);
    } catch {
      return;
    }
    if (!available) throw new Error('凭证名称已存在');
  };

  async function fetchModels() {
    const apiKey = form.getFieldValue('apiKey');
    if (!apiKey) {
      message.info('先填 API Key');
      return;
    }
    try {
      const res = await probe.mutateAsync(apiKey);
      setModels(res.items);
      if (res.items[0] && !form.getFieldValue('defaultModel')) {
        form.setFieldValue('defaultModel', res.items[0].name);
      }
      message.success(`拿到 ${res.items.length} 个模型`);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  async function onOk() {
    const values = await form.validateFields();
    try {
      await createMut.mutateAsync(
        create(CreateMaasCredentialRequestSchema, {
          name: values.name,
          apiKey: values.apiKey,
          defaultModel: values.defaultModel,
        })
      );
      message.success('凭证已创建');
      handleClose();
      onSuccess?.();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  function handleClose() {
    form.resetFields();
    setModels([]);
    onClose();
  }

  return (
    <Modal
      title="新建模型凭证"
      open={open}
      onCancel={handleClose}
      onOk={onOk}
      okText="创建凭证"
      cancelText="取消"
      confirmLoading={createMut.isPending}
      width={520}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={
          {
            baseUrl: DEFAULT_BASE_URL,
          } satisfies Partial<FormValues>
        }
      >
        <Form.Item
          label="凭证名称"
          name="name"
          validateTrigger={['onBlur', 'onSubmit']}
          rules={[{ required: true, message: '凭证名称必填' }, { validator: validateName }]}
        >
          <Input placeholder="例如 prod-deepseek" />
        </Form.Item>

        <Form.Item
          label="API Key"
          name="apiKey"
          rules={[{ required: true, message: 'API Key 必填' }]}
          help={
            <>
              需要先去{' '}
              <a href="https://cloud.infini-ai.com" target="_blank" rel="noreferrer">
                cloud.infini-ai.com
              </a>{' '}
              申请 MaaS API Key
            </>
          }
        >
          <Input.Password
            placeholder="sk-..."
            addonAfter={
              <Button
                type="text"
                size="small"
                loading={probe.isPending}
                onClick={fetchModels}
                icon={<Zap size={12} />}
              >
                获取模型列表
              </Button>
            }
          />
        </Form.Item>

        <Form.Item label="Base URL" name="baseUrl">
          <Input />
        </Form.Item>

        <Form.Item
          label="默认模型"
          name="defaultModel"
          rules={[{ required: true, message: '默认模型必选' }]}
        >
          <MaasModelSelect
            models={models}
            placeholder={models.length === 0 ? '先点「获取模型列表」' : '请选择模型'}
            disabled={models.length === 0}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
